import React, { RefObject, useEffect, useLayoutEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import nclude from "../assets/nclude-logo.svg";
import { normalizeChatMarkdown } from "../utils/normalizeChatMarkdown";

export type ChatMessage = {
  text: string;
  timestamp: string;
  sender: "user" | "bot";
};

type ChatProps = {
  chat: ChatMessage[];
  isStreaming?: boolean;
  assistantName?: string;
  announcementRef?: RefObject<HTMLDivElement>;
  renderMessageActions?: (message: ChatMessage, index: number) => React.ReactNode;
};

function autoLinkMarkdown(text: string) {
  const urlRegex = /(^|\s)((?:https?:\/\/|www\.)[^\s<]+)/gi;
  return text.replace(urlRegex, (_match, prefix, url) => {
    const cleanUrl = url.replace(/[),.;!?]+$/, "");
    const punctuation = url.slice(cleanUrl.length);
    const href = cleanUrl.startsWith("www.") ? `https://${cleanUrl}` : cleanUrl;
    return `${prefix}[${cleanUrl}](${href})${punctuation}`;
  });
}

const Chat = ({
  chat,
  isStreaming = false,
  assistantName = "Assistant",
  announcementRef,
  renderMessageActions,
}: ChatProps) => {
  const endRef = useRef<HTMLDivElement>(null);
  const latestMessageRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef(0);

  const scrollLatestMessageToTop = () => {
    const latestMessage = latestMessageRef.current;
    const scrollContainer = latestMessage?.closest<HTMLElement>(".chat-window-container");
    if (!latestMessage || !scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const messageRect = latestMessage.getBoundingClientRect();
    scrollContainer.scrollTo({
      top: Math.max(0, scrollContainer.scrollTop + messageRect.top - containerRect.top - 12),
      behavior: "auto",
    });
  };

  useLayoutEffect(() => {
    const scrollContainer = endRef.current?.closest(".chat-window-container");
    if (!scrollContainer) return;

    const previousCount = previousMessageCountRef.current;
    const hasNewMessage = chat.length > previousCount;
    const latestMessage = chat[chat.length - 1];

    if (previousCount === 0) {
      scrollContainer.scrollTop = 0;
    } else if (hasNewMessage && latestMessage?.sender === "bot" && latestMessageRef.current) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const messageRect = latestMessageRef.current.getBoundingClientRect();
      const messageTop = scrollContainer.scrollTop + messageRect.top - containerRect.top;
      scrollContainer.scrollTo({ top: Math.max(0, messageTop - 12), behavior: "smooth" });
    } else if (hasNewMessage) {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: "smooth" });
    }

    previousMessageCountRef.current = chat.length;
  }, [chat]);

  useEffect(() => {
    if (!announcementRef?.current || chat.length === 0) return;

    const latestMessage = chat[chat.length - 1];
    if (latestMessage.sender === "user") {
      announcementRef.current.textContent = "Message sent.";
      return;
    }

    if (isStreaming) return;

    const messageBody = latestMessageRef.current?.querySelector<HTMLElement>(".message-body");
    const responseText = (
      messageBody?.innerText ||
      messageBody?.textContent ||
      normalizeChatMarkdown(latestMessage.text)
    ).trim();

    announcementRef.current.textContent = responseText
      ? `${assistantName} response: ${responseText}`
      : `${assistantName} response is ready.`;
  }, [announcementRef, assistantName, chat, isStreaming]);

  return (
    <div className="conversation">
      {chat.map((message, index) => {
        const isAssistant = message.sender === "bot";
        const isActiveStream = isAssistant && isStreaming && index === chat.length - 1;
        const headingId = `conversation-message-heading-${index}`;
        const date = new Date(message.timestamp);
        const formattedTime = Number.isNaN(date.getTime())
          ? ""
          : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

        return (
          <div
            ref={index === chat.length - 1 ? latestMessageRef : undefined}
            className={`conversation-message ${isAssistant ? "assistant-message" : "user-message"}`}
            key={`${message.timestamp}-${index}`}
            tabIndex={index === chat.length - 1 ? 0 : undefined}
            onFocus={index === chat.length - 1 ? scrollLatestMessageToTop : undefined}
          >
            <div className="message-inner">
              <div className="message-avatar" aria-hidden="true">
                {isAssistant ? <img src={nclude} alt="" /> : <span>Y</span>}
              </div>
              <div className="message-content">
                <div className="message-meta">
                  <h4
                    className="message-author"
                    id={headingId}
                  >
                    <span className="visually-hidden">
                      {isAssistant ? `${assistantName} said.` : "You said."}
                    </span>
                    <span aria-hidden="true">{isAssistant ? assistantName : "You"}</span>
                  </h4>
                  {formattedTime && <time dateTime={message.timestamp}>{formattedTime}</time>}
                </div>
                <div className="message-body">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer">
                          {children}
                          <span className="visually-hidden"> (opens in a new tab)</span>
                        </a>
                      ),
                    }}
                  >
                    {autoLinkMarkdown(isAssistant ? normalizeChatMarkdown(message.text) : message.text)}
                  </ReactMarkdown>
                  {isActiveStream && <span className="streaming-cursor" aria-hidden="true" />}
                  {renderMessageActions && renderMessageActions(message, index)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <div ref={endRef} aria-hidden="true" />
    </div>
  );
};

export default Chat;
